import {isBaseMessageCode} from "../models/message-codes";
import {OperationCancelledError} from "../tasks/cancellation-token";

/**
 * Lazy-chunk / dynamic `import()` failures. Angular's esbuild builder splits lazy routes into
 * hashed chunks, so a deploy invalidates the filenames a mid-session user still holds and their
 * next lazy navigation throws. Nothing we can fix from a stack trace.
 *
 * Matched as substrings: every engine words this differently and most append the failing URL.
 */
const ModuleLoadMessages = [
  'Failed to fetch dynamically imported module',              // Chromium/Edge — URL suffixed
  'error loading dynamically imported module',                // Firefox — URL suffixed since FF115; no colon so pre-115 matches too
  'Importing a module script failed',                         // Safari/WebKit — never carries a URL
  'Importing a module script is canceled',                    // Safari — navigated away mid-load
  'is not a valid JavaScript MIME type for module script',    // Safari — a 404'd chunk served the SPA's index.html
  "Failed to execute 'importScripts' on 'WorkerGlobalScope'", // Chromium worker
  'Failed to load worker script at',                          // Firefox worker
];

/**
 * Bare network drops. Anchored rather than substring-matched: these strings are short enough that
 * `includes` would swallow unrelated errors (a legitimate "Payment load failed", say).
 */
const NetworkFailureMessages = [
  /^Failed to fetch$/,                                  // Chromium
  /^Load failed$/,                                      // Safari 17+
  /^NetworkError when attempting to fetch resource\.$/, // Firefox
];

/**
 * Thrown inside scripts we don't ship — iOS in-app browsers and Safari/Chrome extensions injecting
 * into the page. Nothing here is reachable from our source.
 *
 * Substring-matched: each is distinctive enough that `includes` can't swallow a real error, and the
 * engines wrap them differently ("Can't find variable: X" vs "undefined is not an object
 * (evaluating 'X')"), which is why one `__firefox__` entry covers both Firefox-iOS signatures.
 */
const ThirdPartyInjectionMessages = [
  '__firefox__',                             // Firefox iOS content script — reader-mode probe and a bare ReferenceError
  'window.ethereum',                         // crypto-wallet extension probing for a provider we don't expose
  'Invalid call to runtime.sendMessage()',   // an extension talking to a background page that has gone away
  'WKWebView API client did not respond',    // iOS in-app webview bridge dropped a postMessage
];

/**
 * Functions injected by third-party in-app browsers, matched against the stack rather than the
 * message. `findTopmostVisibleElement` is the Google Search app's iOS webview recursing until it
 * blows the stack — what identifies it is the frame, since the message is just the engine's
 * "Maximum call stack size exceeded" wording, which a real bug of ours could equally produce.
 */
const ThirdPartyStackFunctions = ['findTopmostVisibleElement'];

/** Bounds the cause walk so a pathological or self-referential chain can't stall `beforeSend`. */
const MaxCauseDepth = 10;

function messageOf(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }
  if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return null;
}

function nameOf(error: unknown): string | null {
  if (!!error && typeof error === 'object' && 'name' in error && typeof error.name === 'string') {
    return error.name;
  }
  return null;
}

function stackOf(error: unknown): string | null {
  if (!!error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string') {
    return error.stack;
  }
  return null;
}

function causeOf(error: unknown): unknown {
  if (!!error && typeof error === 'object' && 'cause' in error) {
    return error.cause;
  }
  return undefined;
}

function isIgnoredLink(error: unknown): boolean {

  // `OperationCancelledError` never assigns `this.name`, so its instance name is plain 'Error'.
  // Name matching can't see it; `instanceof` is the only handle.
  if (error instanceof OperationCancelledError) {
    return true;
  }

  const stack = stackOf(error);
  if (stack !== null && ThirdPartyStackFunctions.some(fn => stack.includes(fn))) {
    return true;
  }

  const message = messageOf(error);
  if (message === null) {
    return false;
  }

  // `ApiCommandError` is injected by a third-party script and is declared nowhere in this repo, so
  // there is no constructor to compare against — name plus message text is all we have.
  if (nameOf(error) === 'ApiCommandError' && message.includes('request blocked by the browser')) {
    return true;
  }

  // PayPal Honey's Safari extension (`Honey.safariextension/h0.js`), which reports
  // `UnavailableError: UnavailableError` — the message is just the class name, so match on the name
  // and leave the message out of it.
  if (nameOf(error) === 'UnavailableError') {
    return true;
  }

  return ModuleLoadMessages.some(ignored => message.includes(ignored))
    || ThirdPartyInjectionMessages.some(ignored => message.includes(ignored))
    || NetworkFailureMessages.some(ignored => ignored.test(message));
}

/**
 * True when an error is noise we can't act on, and so should never reach Sentry.
 *
 * Walks the `cause` chain, because `MapError` wraps every unrecognized error in `BSR0000` and hangs
 * the original off `cause`. A dynamic-import `TypeError` therefore arrives with its own message
 * reading `[BSR0000]: An Unknown Error Occurred` — inspecting only the top level would never fire.
 */
export function isIgnoredError(error: unknown): boolean {

  // Non-BSR codes (ITK/ATH/NTF/OLNITK/WPS/MPH, including their 0000 generics) are raised
  // server-side, and the server already reports them — capturing them again in the browser just
  // duplicates the issue. So is a null code, which the API withheld on purpose. BSR codes are raised
  // in the browser, so reporting them is on us.
  if (isBaseMessageCode(error) && (error.code === null || !error.code.startsWith('BSR'))) {
    return true;
  }

  const seen = new Set<unknown>();
  let current: unknown = error;

  for (let depth = 0; depth < MaxCauseDepth && current !== undefined && current !== null; depth++) {
    if (typeof current === 'object') {
      if (seen.has(current)) {
        break;
      }
      seen.add(current);
    }
    if (isIgnoredLink(current)) {
      return true;
    }
    current = causeOf(current);
  }

  return false;
}
